import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { promoCodes, type PromoCode, type PromoCreate } from "@/api/promoCodes";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Deposit-bonus promo codes. A code matches a % (or flat) of a qualifying
 * deposit as bonus credit, released after the client trades a volume multiple.
 */
export function AdminPromoCodesPage() {
  const list = useQuery({ queryKey: ["promo-codes"], queryFn: () => promoCodes.list() });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (p: PromoCode) =>
      promoCodes.patch(p.id, { status: p.status === "active" ? "disabled" : "active" }),
    onSuccess: () => {
      toast.push("Promo updated", "success");
      qc.invalidateQueries({ queryKey: ["promo-codes"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Promo codes</h1>
        <Button onClick={() => setOpen(true)}>New code</Button>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No promo codes" description="Create deposit-bonus codes for acquisition campaigns." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Code</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Bonus</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Min deposit</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Redeemed</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((p) => (
                  <tr key={p.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{p.code}</td>
                    <td className="px-3 py-1.5 text-term-dim">
                      {p.percent_match ? `${p.percent_match}%` : p.flat_amount ? `${p.flat_amount} ${p.currency}` : "—"}
                      {p.max_bonus && <span className="text-2xs text-term-mute"> (max {p.max_bonus})</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {p.min_deposit} {p.currency}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {p.redemption_count}
                      {p.redemption_cap ? ` / ${p.redemption_cap}` : ""}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={p.status === "active" ? "good" : "neutral"}>{p.status}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Button variant="ghost" onClick={() => toggle.mutate(p)} disabled={toggle.isPending}>
                        {p.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {open && <PromoModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function PromoModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<PromoCreate>({
    code: "",
    kind: "deposit_match",
    percent_match: "50",
    currency: "USD",
    min_deposit: "100",
    volume_lot_multiple: "1",
    status: "active",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => promoCodes.create(form),
    onSuccess: () => {
      toast.push("Promo code created", "success");
      qc.invalidateQueries({ queryKey: ["promo-codes"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="New promo code">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Code</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="WELCOME50"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="deposit_match">Deposit match</option>
              <option value="flat">Flat bonus</option>
            </Select>
          </div>
          <div>
            <Label>Currency</Label>
            <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Percent match</Label>
            <Input
              value={String(form.percent_match ?? "")}
              onChange={(e) => setForm({ ...form, percent_match: e.target.value })}
              placeholder="50"
            />
          </div>
          <div>
            <Label>Flat amount</Label>
            <Input
              value={String(form.flat_amount ?? "")}
              onChange={(e) => setForm({ ...form, flat_amount: e.target.value || null })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Min deposit</Label>
            <Input
              value={String(form.min_deposit ?? "")}
              onChange={(e) => setForm({ ...form, min_deposit: e.target.value })}
            />
          </div>
          <div>
            <Label>Volume lot multiple</Label>
            <Input
              value={String(form.volume_lot_multiple ?? "")}
              onChange={(e) => setForm({ ...form, volume_lot_multiple: e.target.value })}
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
